import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Printer, Download, Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const PublicInvoicePage = () => {
  const { invoiceId } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchInvoice = async () => {
      try {
        const res = await axios.get(`${API}/api/public/invoice/${invoiceId}`);
        setInvoice(res.data);
      } catch (e) {
        setError("Invoice not found");
      } finally {
        setLoading(false);
      }
    };
    if (invoiceId) fetchInvoice();
  }, [invoiceId]);

  const handlePrint = () => window.print();

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-100 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-stone-400" />
      </div>
    );
  }

  if (error || !invoice) {
    return (
      <div className="min-h-screen bg-stone-100 flex items-center justify-center">
        <div className="text-center">
          <p className="text-xl text-stone-500 mb-4">Invoice not found</p>
          <a href="/" className="text-emerald-600 underline">Go to homepage</a>
        </div>
      </div>
    );
  }

  const num = String(invoice.invoice_number).padStart(4, "0");
  const date = invoice.created_at ? new Date(invoice.created_at).toLocaleDateString() : "";
  const subtotal = parseFloat(invoice.subtotal) || 0;
  const total = parseFloat(invoice.total) || 0;

  return (
    <div className="min-h-screen bg-stone-100 py-8 px-4" data-testid="public-invoice-page">
      {/* Print/PDF buttons - hidden on print */}
      <div className="max-w-[700px] mx-auto mb-4 flex justify-end gap-2 print:hidden">
        <Button variant="outline" size="sm" onClick={handlePrint}>
          <Download className="w-4 h-4 mr-2" /> Save as PDF
        </Button>
        <Button variant="outline" size="sm" onClick={handlePrint}>
          <Printer className="w-4 h-4 mr-2" /> Print
        </Button>
      </div>

      {/* Invoice */}
      <div className="max-w-[700px] mx-auto bg-white shadow-lg rounded-lg p-10 print:shadow-none print:rounded-none print:p-8">
        {/* Header */}
        <div className="flex justify-between items-start mb-5 pb-4 border-b-[3px] border-[#c45c2c]">
          <div className="text-center">
            <img src="/assets/logo-sally-light.jpg" alt="SALLY Better" className="h-20 w-auto rounded-md block mx-auto" />
            <a href="https://www.sallybetter.com" target="_blank" rel="noreferrer" className="block mt-1.5 text-[#c45c2c] text-xs hover:underline text-center">www.sallybetter.com</a>
          </div>
          <div className="text-right">
            <p className="font-bold text-[#c45c2c] text-lg">Invoice #{num}</p>
            <p className="text-stone-500 text-sm">Date: {date}</p>
          </div>
        </div>

        {/* Billed to */}
        <div className="mb-6">
          <p className="text-stone-400 text-xs uppercase tracking-wider">Invoice for:</p>
          <p className="font-bold text-lg mt-1">{invoice.client_name}</p>
          {invoice.client_email && <p className="text-stone-500 text-sm">{invoice.client_email}</p>}
        </div>

        {/* Items Table */}
        <table className="w-full mb-6">
          <thead>
            <tr className="bg-[#c45c2c] text-white">
              <th className="text-left p-3 text-sm">Description</th>
              <th className="text-center p-3 text-sm w-[70px]">Qty.</th>
              <th className="text-right p-3 text-sm w-[100px]">Price</th>
              <th className="text-right p-3 text-sm w-[100px]">Total</th>
            </tr>
          </thead>
          <tbody>
            {(invoice.items || []).map((item, idx) => (
              <tr key={idx} className="border-b border-stone-100">
                <td className="p-3 text-sm">{item.description}</td>
                <td className="p-3 text-sm text-center">{item.qty}</td>
                <td className="p-3 text-sm text-right">${(parseFloat(item.price) || 0).toFixed(2)}</td>
                <td className="p-3 text-sm text-right">${(parseFloat(item.total) || 0).toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Totals */}
        <div className="text-right mb-6">
          <p className="text-sm mb-1"><strong>Sub-total:</strong> ${subtotal.toFixed(2)}</p>
          <p className="text-2xl font-bold text-[#c45c2c]">Total: ${total.toFixed(2)}</p>
        </div>

        {/* Footer */}
        <div className="flex justify-between border-t border-stone-200 pt-4">
          <div>
            {invoice.payment_method && <p className="text-sm"><strong>Payment method:</strong> {invoice.payment_method}</p>}
            {invoice.notes && <p className="text-xs text-stone-500 mt-1">{invoice.notes}</p>}
          </div>
        </div>

        <p className="text-center text-stone-400 mt-6 italic">Thank You!</p>
      </div>
    </div>
  );
};

export default PublicInvoicePage;
